import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { AgentProgressEvent } from '../types/agentTypes';

interface OttoThinkingProps {
  progress?: AgentProgressEvent;
  size?: number;
}

/**
 * Get user-friendly message based on tool name
 */
const getToolMessage = (toolName?: string): string => {
  if (!toolName) {
    return 'Thinking...';
  }

  const messages: Record<string, string> = {
    updateIngredients: 'Adding ingredients...',
    addStep: 'Writing cooking steps...',
    updateStepContent: 'Updating instructions...',
    removeStep: 'Removing step...',
    reorderSteps: 'Reorganizing steps...',
    updateRecipeMetadata: 'Updating recipe details...',
  };

  return messages[toolName] || `Working on ${toolName}...`;
};

/**
 * Get animation speed (interval) based on phase
 */
const getAnimationSpeed = (phase: AgentProgressEvent['phase']): number => {
  switch (phase) {
    case 'thinking':
      return 300; // Faster animation during thinking
    case 'calling_tool':
      return 500; // Medium speed during tool calls
    case 'tool_complete':
      return 700; // Slower after tool completion
    case 'done':
      return 0; // No animation when done
    default:
      return 500;
  }
};

const OttoThinking: React.FC<OttoThinkingProps> = ({ progress, size = 100 }) => {
  const [frame, setFrame] = useState(1);

  // Animate through frames with variable speed
  useEffect(() => {
    if (!progress || progress.phase === 'done') {
      // Show "Idea!" frame when done and hold it
      setFrame(5);
      // Auto-hide after 1 second
      const timeout = setTimeout(() => setFrame(0), 1000);
      return () => clearTimeout(timeout);
    }

    const speed = getAnimationSpeed(progress.phase);

    // Loop through thinking frames (1-4)
    const interval = setInterval(() => {
      setFrame(prev => {
        if (prev >= 4) {
          return 1; // Loop frames 1-4
        }
        return prev + 1;
      });
    }, speed);

    return () => clearInterval(interval);
  }, [progress?.phase]);

  // Don't render if no progress or hidden
  if (!progress || frame === 0) {
    return null;
  }

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        zIndex: 1300, // Below modals (1400) but above most content
        p: 2,
        borderRadius: 2,
        backgroundColor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minWidth: 200,
      }}
    >
      <img
        src={`/assets/images/otto/otto_think_${frame}.svg`}
        alt="Otto is thinking..."
        width={size}
        height={size}
      />
      <Box>
        <Typography variant="body2" fontWeight="bold">
          {progress.phase === 'calling_tool' || progress.phase === 'tool_complete'
            ? getToolMessage(progress.toolName)
            : 'Thinking...'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {progress.phase === 'done' ? '✓ Complete!' : `Step ${progress.iteration}`}
        </Typography>
      </Box>
    </Paper>
  );
};

export default OttoThinking;
